import ChipFilters from '@/features/shared/ElasticComponent/ChipFilters';
import ElasticComponentContext from '@/features/shared/ElasticComponent/ElasticComponentContext';
import { useDataLayerSearchEvents } from '@/features/shared/Hooks/data-layer';
import { trackSearchClick } from '@/features/shared/Hooks/useElasticSearch';
import {
  MuiHidden,
  MuiPagination,
  MuiPaginationItem,
  MuiPaper,
  MuiTypography,
} from '@/features/shared/material-ui';
import { CaptureClicksRichText } from '@/features/shared/RichText';
import { SearchMeta } from '@/features/shared/SearchMeta';
import { ColorNames, IS_PREVIEW, LoadingStatusValues, Theme } from '@/utils/constants';
import { escapeHtml, formatText, setPageLink } from '@/utils/helpers';
import clsx from 'clsx';
import React, { ChangeEvent, memo, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { CardResult } from './Card';
import { CardResultProps } from './Card/Card.interface';
import CardSkeleton from './Card/CardSkeleton';
import { HeaderContent } from './Header';
import { HeaderMobileContent } from './HeaderMobile';
import { SearchResultProps } from './SearchResult.interface';
import { SearchResultPlaceholder } from './SearchResult.placeholder';
import useStyles from './SearchResult.styles';
import { Sidebar } from './Sidebar';

const SearchResult = ({
  contentButtons,
  content,
  categoryUrl,
  presentation = {},
  labels,
}: Br.PropsWithContentButtons<SearchResultProps>) => {
  const classes = useStyles({});
  const {
    status,
    queryParams,
    updateQueryParams,
    results = [],
    pagination,
    labels: esLabels = {},
    engine,
    requestID,
    searchKey = '',
  } = useContext(ElasticComponentContext);
  const { triggerSearchEventDataLayer } = useDataLayerSearchEvents('page_nav_click');

  const handlePagination = (event: ChangeEvent<unknown>, value: number) => {
    event.preventDefault();

    updateQueryParams('page', String(value), false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    triggerSearchEventDataLayer();
  };

  const QUERY = useMemo(() => (queryParams?.q ? escapeHtml(queryParams.q) : ''), [queryParams?.q]);

  const NO_RESULTS_MESSAGE = useMemo(
    () =>
      formatText(esLabels.sorry_no_results || 'Sorry, we couldn’t find any results for “%1”', [
        QUERY,
      ]),
    [esLabels.sorry_no_results, QUERY],
  );

  const handleResultClicks = (evt: React.MouseEvent) => {
    const target = evt.target as HTMLElement;
    const a = target?.closest('a');

    if (a?.dataset?.resultid) {
      const { resultid } = a.dataset;
      trackSearchClick(searchKey, QUERY, engine, resultid, requestID);
    }
  };

  const { loading, ready } = LoadingStatusValues;
  const isLoading = React.useMemo(
    () => status === loading && !content?.isCached,
    [status, loading, content?.isCached],
  );
  const isCMSPreview = IS_PREVIEW && results.length < 1;
  const showNoResults = results.length === 0 && !IS_PREVIEW;

  return (
    <section className={classes.root} data-testid="search-result--container">
      {contentButtons?.content}
      <div className={classes.sidebar}>
        <Sidebar />
      </div>
      <div className={classes.content}>
        <div className={clsx(classes.block, classes.headerDesktop)}>
          <HeaderContent isSortShown={presentation?.isSortShown} query={QUERY} />
        </div>
        {content?.detailContent && (
          <div>
            <MuiPaper elevation={0} className={classes.partnerMessage} square>
              <CaptureClicksRichText
                customStyles
                content={content.detailContent}
                className={classes.detailContent}
              />
            </MuiPaper>
          </div>
        )}
        <div className={classes.headerMobile}>
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/* @ts-ignore */}
          <MuiHidden implementation="css" mdUp>
            <div className={clsx(classes.block, 'header')}>
              <HeaderMobileContent labels={labels} />
            </div>
          </MuiHidden>
        </div>

        <div className={classes.tags}>
          <ChipFilters chipColor={ColorNames.charcoal} hoverable={false} theme={Theme.dark} />
        </div>

        {isCMSPreview && <SearchResultPlaceholder theme={Theme.light} />}

        {/* This <div> is handling bubbled clicks */}
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
        <div onClick={handleResultClicks}>
          {isLoading && (
            <>
              {new Array(2).fill(null).map((item, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <div className={classes.block} key={`block_${idx}`}>
                  <CardSkeleton theme={Theme.light} cardColor={ColorNames.white} />
                </div>
              ))}
            </>
          )}
          {!isLoading && (
            <>
              {showNoResults && <MuiTypography variant="h5">{NO_RESULTS_MESSAGE}</MuiTypography>}

              {results.map((item: CardResultProps, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <div className={clsx(classes.block, 'gtm-result')} key={`block_${idx}`}>
                  <CardResult
                    contentCategory={item.contentCategory}
                    title={item.title}
                    image={item.image}
                    description={item.description}
                    tags={item.tags}
                    date={item.date}
                    url={item.url}
                    theme={Theme.light}
                    category={item.category}
                    categoryType={item.categoryType}
                    categoryUrl={categoryUrl}
                    cardColor={ColorNames.white}
                    resultID={item.resultID}
                    isPartnerOnly={item.isPartnerOnly}
                    download={item.download}
                  />
                </div>
              ))}
              <SearchMeta paging={pagination} query={queryParams} />
            </>
          )}
        </div>
        {pagination?.totalPages > 1 && (
          <div>
            <MuiPagination
              data-datalayer-position="search_result_pagination"
              className={clsx(classes.pagination, 'gtm-pagination-container')}
              count={pagination?.totalPages}
              page={pagination?.currentPage}
              onChange={handlePagination}
              renderItem={(item: any) => (
                <MuiPaginationItem component={Link} to={setPageLink(item.page)} {...item} />
              )}
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default memo(SearchResult);
